<template>
  <el-card body-style="padding: 0">
    <div slot="header" class="card_header">
      <span>篩檢回報統計 {{ department ? `部門：${department}` : '' }} </span>
    </div>
    <div class="button_container">
      <div class="filter-wrapper">
        <div class="sm:text-8 md:text-16 flex items-center h-full px-4">回報日期：</div>
        <el-date-picker class="w-150px" v-model="startDate" placeholder="開始日期"/>
        <span style="width: 30px;text-align: center">到</span>
        <el-date-picker class="w-150px" v-model="endDate" placeholder="结束日期"/>
        <el-button type="primary" icon="el-icon-refresh-right"
                   class="ml-10px" @click="getStatData">
          重查
        </el-button>
      </div>
    </div>
    <ezoom-table
        title="快篩回報統計"
        :has-no="true"
        :data="filteredData"
        :row-style="{cursor:'pointer'}"
        :columns="statColumns"
        @row-click="openDetails"
        v-loading="loading"
        :default-sort="{prop:'report_date',order:'descending'}">
      <el-table-column prop="report_date" sortable label="回報日期" width="125"/>
      <el-table-column prop="totalNum" label="回報人數" width="125"/>

      <template v-for="rtType in rapidTestSettings">
        <el-table-column :key="`${rtType.order}_num`"
                         :prop="`${rtType.label}_num`"
                         :label="`${rtType.label} 人數`"
                         width="130"/>
        <el-table-column :key="`${rtType.order}_pos`"
                         :prop="`${rtType.label}_pos`"
                         :label="`${rtType.label} 陽性人數`"
                         width="130"/>
      </template>

    </ezoom-table>
  </el-card>
</template>

<script>
import {ref, computed, onMounted} from "@vue/composition-api";
import {department} from "@/views/useDepartment";
import EzoomTable from "@/components/EzoomTable";
import DateUtil from "@/utils/dateUtil";
import RapidTestService from "@/api/rapidTest";
import RapidTestSettingService from "@/api/rapidTestSetting";
import {openDetails} from "./useRapidTest";

export default {
  name: "RapidTestStatistic",
  components: {
    EzoomTable
  },
  setup() {

    const rapidTestStats = ref([]);
    const rapidTestSettings = ref([]);
    const statData = ref([]);
    const statColumns = ref([]);
    const loading = ref(true);
    const startDate = ref(null);
    const endDate = ref(new Date());

    const filteredData = computed(() => {

      return [...statData.value]
          .sort((a, b) => b.timestamp - a.timestamp)
          .filter(row => {

            if (startDate.value) return row.date >= startDate.value;
            else return true;
          })
          .filter(row => {

            if (endDate.value) return row.date <= endDate.value;
            else return true;
          });
    });

    const getStatData = async () => {

      loading.value = true;

      rapidTestStats.value = await RapidTestService.statistic({department: department.value});

      const groupByRtType = stats => {

        const result = {};

        // 利用 dose 跟 deptName 這兩個欄位做 grouping
        for (const stat of stats) {

          const getReportDate = date => `${DateUtil.format(date, "YYYY/MM/DD")}(${DateUtil.getWeekDayStr(date)})`;
          const report_date = getReportDate(stat.day);
          const empNum = stat.employeeNum;
          const reportedSum = stat.reportedSum;
          const date = stat.day && DateUtil.toDate(stat.day, 'YYYY-MM-DD');
          const timestamp = date && date.getTime();

          const singleType = {
            rapidTestType: stat.rapidTestType,
            testNum: stat.testNum,
            positiveNum: stat.positiveNum,
          }

          if (result[report_date]) result[report_date].typeArr.push(singleType);
          else result[report_date] = {report_date, reportedSum, date, timestamp, empNum, typeArr: [singleType]};
        }

        return Object.values(result);
      }

      const groupedStats = groupByRtType(rapidTestStats.value);

      statData.value = groupedStats.map(single => {

        const rtTypes = rapidTestSettings.value;
        const typeArr = single.typeArr;

        const result = {
          report_date: single.report_date,
          date: single.date,
          timestamp: single.timestamp,
          totalNum: single.reportedSum,
        };

        for (const reType of rtTypes) {
          const targetObj = typeArr.find(obj => obj.rapidTestType === reType.label)
          result[`${reType.label}_num`] = targetObj ? targetObj.testNum : 0;
          result[`${reType.label}_pos`] = targetObj ? targetObj.positiveNum : 0;
        }

        return result
      });

      loading.value = false;
    };

    const init = async () => {

      loading.value = true;

      rapidTestSettings.value = (await RapidTestSettingService.list()).sort((a, b) => a.order - b.order);
      await getStatData();

      const getRenderColumns = rtSettings => {

        return rtSettings
            .map(rtType => [
              {label: `${rtType.label} 人數`, prop: `${rtType.label}_num`},
              {label: `${rtType.label} 陽性人數`, prop: `${rtType.label}_pos`}
            ])
            .reduce((pre, curr) => [...pre, ...curr], []);
      }

      statColumns.value = [
        {label: "回報日期", prop: "report_date"},
        {label: "回報人數", prop: "totalNum"},
        ...getRenderColumns(rapidTestSettings.value)
      ]

      loading.value = false;
    };

    onMounted(() => init().then().catch(e => console.error(e)))

    return {
      getStatData,
      openDetails,
      loading,
      department,
      startDate,
      endDate,
      statData,
      rapidTestStats,
      filteredData,
      rapidTestSettings,
      statColumns,
    }
  },
}
</script>

<style scoped>

.filter-wrapper {
  display: flex;
  align-items: center;
}

.card_header {
  display: flex;
  justify-content: space-between;
  font-size: 24px;
  font-weight: bolder;
}

.button_container {
  display: flex;
  flex-direction: column;
  padding: 10px;
}
</style>
